// Revenue-goal ViewModel - selected bracket state and dispatchers for the
// onboarding revenue-target slider. The answer is saved as a generic onboarding
// response (questionId 'revenue-goal-12mo', textValue = bracket position "1".."5").

import { useCallback, useMemo, useState } from 'react';
import { Actions, Dispatchers } from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';

// Frontend-defined questionId for the generic onboarding-responses endpoint.
export const REVENUE_GOAL_QUESTION_ID = 'revenue-goal-12mo';

type RevenueBracket = {
  position: number;
  amount: string;
};

export const REVENUE_BRACKETS: readonly RevenueBracket[] = [
  { position: 1, amount: 'Under $10K' },
  { position: 2, amount: '$10K to $50K' },
  { position: 3, amount: '$50K to $100K' },
  { position: 4, amount: '$100K to $500K' },
  { position: 5, amount: '$500K+' },
];

export const MIN_REVENUE_POSITION = 1;
export const MAX_REVENUE_POSITION = 5;
// Middle bracket: the default the slider starts on.
export const DEFAULT_REVENUE_POSITION = 3;

export const clampPosition = (position: number): number => {
  if (Number.isNaN(position)) return DEFAULT_REVENUE_POSITION;
  return Math.min(
    MAX_REVENUE_POSITION,
    Math.max(MIN_REVENUE_POSITION, position),
  );
};

const getBracket = (position: number): RevenueBracket => {
  const clamped = clampPosition(position);
  return (
    REVENUE_BRACKETS.find((bracket) => bracket.position === clamped) ??
    REVENUE_BRACKETS[DEFAULT_REVENUE_POSITION - 1]
  );
};

type RevenueGoalActionMap = {
  SetPosition: number;
};

type RevenueGoalState = {
  position: number;
  bracket: RevenueBracket;
};

type RevenueGoalVM = {
  state: RevenueGoalState;
} & Dispatchers<RevenueGoalActionMap>;

export const useRevenueGoalViewModel = (
  initialPosition: number = DEFAULT_REVENUE_POSITION,
): RevenueGoalVM => {
  const [position, setPosition] = useState(() =>
    clampPosition(initialPosition),
  );

  const _dispatch = useCallback((action: Actions<RevenueGoalActionMap>) => {
    switch (action.type) {
      case 'SetPosition':
        setPosition(clampPosition(action.payload));
        return;
    }
  }, []);

  const dispatchers = useMemo(
    () => getDispatchers<RevenueGoalActionMap>(_dispatch),
    [_dispatch],
  );

  return {
    state: {
      position,
      bracket: getBracket(position),
    },
    ...dispatchers,
  };
};
